/** The arithmetic under the uploads graph, kept apart from the drawing.
 *
 * A column chart is three small decisions — where the scale stops, which
 * ticks it shows, and which of thirty buckets get a label — and each is easy
 * to get subtly wrong in a way only a particular day's data would reveal: a
 * scale that stops at 7 for a peak of 7 and leaves the tallest column touching
 * the frame, a tick at 2.5 uploads, a month of labels printed on top of each
 * other. They are pure, so they are tested without a DOM.
 */

import type { UploadTimelineBucket, UploadTimelineUnit } from '@/lib/types';

/** The four places an accepted upload can stand, bottom of the stack first. */
export const TIMELINE_SERIES = ['merged', 'pending', 'held', 'rejected'] as const;
export type TimelineSeries = (typeof TIMELINE_SERIES)[number];

/**
 * What each is called and what it wears. `pending` is "queued" on this chart:
 * it is waiting for the merge job, which is what the word means to a reader
 * who has not read the schema. The colours are the ones the Uploads table's
 * status tags already wear — merged in the accent, queued neutral, held and
 * rejected in the two warm state colours — so the chart and the table agree.
 * Checked against the surface with the queued neutral at 300: every pair is
 * apart under deuteranopia, protanopia and tritanopia (worst ΔE 11), and the
 * legend and the tooltip name each one, so nothing is carried by hue alone.
 */
export const SERIES_META: Record<TimelineSeries, { label: string; colour: string }> = {
  merged: { label: 'Merged', colour: 'var(--color-accent)' },
  pending: { label: 'Queued', colour: 'var(--color-neutral-300)' },
  held: { label: 'Held', colour: 'var(--color-warn)' },
  rejected: { label: 'Rejected', colour: 'var(--color-error)' },
};

export function bucketTotal(bucket: UploadTimelineBucket): number {
  return bucket.merged + bucket.pending + bucket.held + bucket.rejected;
}

/**
 * The top of the scale for a peak: the next of 4, 8, 12, 20 × 10ⁿ at or above
 * it. Those and no others, because the scale is cut into four — a top of 10
 * would put a tick at 2.5 uploads — and each of them makes the step a round
 * 1, 2, 3 or 5.
 */
export function niceMax(peak: number): number {
  if (!Number.isFinite(peak) || peak <= 4) return 4;
  for (let magnitude = 1; ; magnitude *= 10) {
    for (const step of [4, 8, 12, 20]) {
      if (step * magnitude >= peak) return step * magnitude;
    }
  }
}

/** Four even intervals from zero: whole numbers, because `niceMax` made it so. */
export function ticksFor(max: number): number[] {
  return [0, max / 4, max / 2, (max * 3) / 4, max];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** `15:00`, or `Sun 20 Sep` — in UTC, which is how the buckets were cut. */
export function bucketLabel(at: string, unit: UploadTimelineUnit): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  if (unit === 'hour') return `${String(date.getUTCHours()).padStart(2, '0')}:00`;
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

/** The same, with the day on an hour so a tooltip at 01:00 says which night. */
export function bucketTitle(at: string, unit: UploadTimelineUnit): string {
  if (unit === 'day') return bucketLabel(at, 'day');
  return `${bucketLabel(at, 'day')}, ${bucketLabel(at, 'hour')}–${bucketLabel(
    new Date(Date.parse(at) + 3600_000).toISOString(),
    'hour',
  )}`;
}

/** The shorter form the axis has room for: `15:00`, `20 Sep`. */
export function axisLabel(at: string, unit: UploadTimelineUnit): string {
  if (unit === 'hour') return bucketLabel(at, 'hour');
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

/**
 * Which buckets carry an axis label: every third hour of a day, every day of
 * a week, every fifth day of a month — counted back from the newest, so the
 * right edge, which is now, is always named.
 */
export function labelledIndexes(count: number, unit: UploadTimelineUnit): Set<number> {
  const every = unit === 'hour' ? 3 : count > 10 ? 5 : 1;
  const out = new Set<number>();
  for (let index = count - 1; index >= 0; index -= every) out.add(index);
  return out;
}
